import { cx } from '@flowus/common/cx';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { CollectionViewType } from '@next-space/fe-api-idl';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useBitable } from '../context';
import { FormLogicSetting } from '../form-view/form-logic-setting';
import { ConfigTableFilter } from './config-table-filter';
import { ConfigTableSort } from './config-table-sort';
import { GroupList } from './group-list';
import { PropertyList } from './property-list';
import { SubItemMenu } from './subitem-menu';
import type { SettingProps } from './type';
import { ViewSetting } from './view-setting';

export enum ItemType {
  VIEW = 'view',
  PROPERTY = 'property',
  FILTER = 'filter',
  SORT = 'sort',
  GROUP = 'group',
  SUB_GROUP = 'sub_group',
  FORM_LOGIC = 'form_logic',
  SUBITEM = 'subitem',
}

export const ViewSettingPanel: FC<SettingProps & { defaultItemType?: ItemType }> = (props) => {
  const { viewType } = useBitable();
  const [currentType, setCurrentType] = useState<ItemType>(props.defaultItemType ?? ItemType.VIEW);
  const { t } = useAppTranslation();

  const items = useMemo(() => {
    const baseItems = [
      { name: t('bitable.view.title'), type: ItemType.VIEW, Component: ViewSetting },
      { name: t('common.property'), type: ItemType.PROPERTY, Component: PropertyList },
      { name: t('common.filter'), type: ItemType.FILTER, Component: ConfigTableFilter },
      { name: t('common.sort'), type: ItemType.SORT, Component: ConfigTableSort },
      { name: t('bitable.embed.group'), type: ItemType.GROUP, Component: GroupList },
      { name: t('bitable.subitem.title'), type: ItemType.SUBITEM, Component: SubItemMenu },
    ];

    let result = [...baseItems];
    if (viewType === CollectionViewType.FORM) {
      result = baseItems.filter(
        (item) => item.type === ItemType.VIEW || item.type === ItemType.PROPERTY
      );
      result.push({
        name: t('bitable.view.form_logic'),
        type: ItemType.FORM_LOGIC,
        Component: FormLogicSetting,
      });
    } else if (viewType === CollectionViewType.BOARD) {
      result.push({
        name: t('bitable.embed.subbox'),
        type: ItemType.SUB_GROUP,
        Component: GroupList,
      });
    } else if (viewType === CollectionViewType.CALENDAR) {
      result = baseItems.filter((item) => item.type !== ItemType.GROUP);
    }
    return result;
  }, [t, viewType]);

  const Component = items.find((item) => item.type === currentType)?.Component;
  if (!Component) return null;

  return (
    <div className={'next-modal-scroll pt-[5px] min-w-[400px]'}>
      <div className="border-b border-grey6 py-1.5 px-2 flex">
        {items.map((item) => {
          return (
            <button
              key={item.type}
              onClick={() => setCurrentType(item.type)}
              className={cx(
                'mr-2 rounded-sm py-1.5 px-2 animate-hover-black3 text-t2',
                item.type === currentType && 'bg-black_006'
              )}
            >
              {item.name}
            </button>
          );
        })}
      </div>

      <Component
        {...props}
        isSubGroup={currentType === ItemType.SUB_GROUP}
        isFromViewSetting={true}
      />
    </div>
  );
};
